import CoinIcon from '../components/CoinIcon';
import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import api from '../api';
import { FiSearch, FiMapPin, FiArrowLeft, FiChevronLeft, FiChevronRight, FiImage, FiStar, FiCheckCircle, FiX, FiSliders, FiFilter, FiChevronDown } from 'react-icons/fi';
import { expandLocationSearch } from '../utils/locationData';

const ALL_CATEGORIES = ['Catering', 'Decoration', 'Photography', 'Makeup Artists', 'Venues', 'Car Hire', 'Entertainment', 'Event Planners', 'Gifts & Gift Shops', 'Printing Services', 'Other'];

const PRICE_RANGES = [
  { label: 'Budget Friendly', min: 0, max: 10000 },
  { label: 'Economy', min: 10000, max: 25000 },
  { label: 'Standard', min: 25000, max: 50000 },
  { label: 'Premium', min: 50000, max: 100000 },
  { label: 'Luxury', min: 100000, max: Infinity },
];

const EVENT_TYPES = ['Wedding', 'Birthday', 'Corporate', 'Baby Shower', 'Lobola', 'Funeral', 'Other'];

function parsePriceMax(priceRangeStr) {
  if (!priceRangeStr) return 0;
  const cleaned = priceRangeStr.replace(/[Rr]/g, '').replace(/\/.*$/, '');
  const parts = cleaned.split('-').map(s => Number(s.trim().replace(/,/g, '')));
  if (parts.length >= 2 && !isNaN(parts[1])) return parts[1];
  if (parts.length === 1 && !isNaN(parts[0])) return parts[0];
  return 0;
}

function locationMatches(vendorLoc, vendorServiceAreas, searchTerm) {
  const term = searchTerm.toLowerCase().trim();
  if (!term) return true;

  // expandLocationSearch returns all matching areas, siblings, and city names
  // e.g., "pretoria" → {pretoria, centurion, ga-rankuwa, soshanguve, hammanskraal, ...}
  const expanded = expandLocationSearch(term);

  // Check business_location (string)
  const locStr = (vendorLoc || '').toLowerCase();
  for (const candidate of expanded) {
    if (locStr.includes(candidate)) return true;
  }

  // Check service_areas (array of strings)
  const areas = Array.isArray(vendorServiceAreas) ? vendorServiceAreas : [];
  for (const area of areas) {
    const areaLower = (area || '').toLowerCase();
    for (const candidate of expanded) {
      if (areaLower.includes(candidate)) return true;
    }
  }

  // Direct substring match as fallback (handles partial typed input)
  for (const area of areas) {
    if ((area || '').toLowerCase().includes(term)) return true;
  }
  if (locStr.includes(term)) return true;

  return false;
}

function VendorCard({ vendor, staggerIndex = 0 }) {
  const [imgIdx, setImgIdx] = useState(0);
  const mediaItems = vendor.portfolio?.length > 0
    ? vendor.portfolio.map(p => ({ url: p.image_url, type: p.media_type || 'image' }))
    : vendor.business_gallery?.length > 0
      ? vendor.business_gallery.map(url => ({ url, type: 'image' }))
      : [];
  const hasImages = mediaItems.length > 0;
  const rating = Number(vendor.avg_rating || 0);
  const priceMax = parsePriceMax(vendor.business_price_range);

  return (
    <Link to={`/vendors/${vendor.id}`} className={`card card-enter card-stagger-${(staggerIndex % 12) + 1}`} style={{ padding: 0, overflow: 'hidden', textDecoration: 'none', color: 'inherit', transition: 'transform 0.2s, box-shadow 0.2s', cursor: 'pointer', display: 'flex', flexDirection: 'column', minHeight: '380px' }}
      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = 'var(--shadow-xl)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = ''; }}
    >
      <div className="card-img-area" style={{ position: 'relative', aspectRatio: '4/3', overflow: 'hidden', background: hasImages ? 'transparent' : 'linear-gradient(135deg, var(--rose-light), var(--dark-light))' }}>
        {hasImages ? (
          <>
            {mediaItems[imgIdx]?.type === 'video' ? (
              <video src={mediaItems[imgIdx].url} style={{ width: '100%', height: '100%', objectFit: 'cover' }} muted loop autoPlay playsInline />
            ) : (
              <img src={mediaItems[imgIdx]?.url} alt={vendor.business_name} style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'opacity 0.3s' }} loading="lazy" />
            )}
            {mediaItems.length > 1 && (
              <div style={{ position: 'absolute', bottom: '10px', left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: '5px' }}>
                {mediaItems.map((_, i) => (
                  <button key={i} onClick={e => { e.preventDefault(); e.stopPropagation(); setImgIdx(i); }} style={{
                    width: '8px', height: '8px', borderRadius: '50%', border: 'none',
                    background: i === imgIdx ? 'var(--white)' : 'rgba(255,255,255,0.4)',
                    cursor: 'pointer', transition: 'all 0.2s', padding: 0,
                    transform: i === imgIdx ? 'scale(1.3)' : 'scale(1)',
                    boxShadow: i === imgIdx ? '0 0 6px rgba(255,255,255,0.5)' : 'none'
                  }} />
                ))}
              </div>
            )}
            {mediaItems.length > 1 && (
              <>
                <button onClick={e => { e.preventDefault(); e.stopPropagation(); setImgIdx(i => i > 0 ? i - 1 : mediaItems.length - 1); }} style={{
                  position: 'absolute', left: '8px', top: '50%', transform: 'translateY(-50%)',
                  background: 'rgba(0,0,0,0.5)', color: 'var(--white)', border: 'none', borderRadius: '50%',
                  width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer'
                }}><FiChevronLeft size={18} /></button>
                <button onClick={e => { e.preventDefault(); e.stopPropagation(); setImgIdx(i => i < mediaItems.length - 1 ? i + 1 : 0); }} style={{
                  position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)',
                  background: 'rgba(0,0,0,0.5)', color: 'var(--white)', border: 'none', borderRadius: '50%',
                  width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer'
                }}><FiChevronRight size={18} /></button>
              </>
            )}
            <div style={{ position: 'absolute', top: '10px', right: '10px', background: 'rgba(0,0,0,0.5)', color: 'var(--white)', borderRadius: '6px', padding: '3px 8px', fontSize: '11px', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <FiImage size={11} /> {mediaItems.length}
            </div>
          </>
        ) : (
          <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.6)' }}>
            <FiImage size={40} />
          </div>
        )}
        <div style={{ position: 'absolute', bottom: '10px', left: '12px' }}>
          <span style={{ background: 'rgba(255,255,255,0.92)', color: '#1c1c1e', backdropFilter: 'blur(4px)', fontSize: '11px', padding: '3px 10px', borderRadius: '12px', fontWeight: 500 }}>{vendor.business_category || 'Vendor'}</span>
        </div>
        {rating >= 4.5 && (
          <div style={{ position: 'absolute', top: '10px', left: '10px', background: 'var(--rose)', color: 'var(--white)', borderRadius: '6px', padding: '3px 8px', fontSize: '10px', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '3px' }}>
            <FiStar size={10} fill="var(--white)" /> TOP RATED
          </div>
        )}
      </div>

      <div className="card-body" style={{ padding: '12px 16px', flex: 1, display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '8px', marginBottom: '2px' }}>
          <h3 style={{ fontSize: '15px', margin: 0, lineHeight: 1.3, flex: 1 }}>{vendor.business_name || vendor.name}</h3>
          {priceMax > 0 && (
            <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--dark)', whiteSpace: 'nowrap' }}>
              <span style={{ fontSize: '10px', fontWeight: 400, color: 'var(--gray)' }}>from </span>
              R{priceMax.toLocaleString()}
            </span>
          )}
        </div>
        {vendor.is_founding_vendor && <span title="Founding Vendor" style={{ display: 'inline-flex', alignItems: 'center', gap: '2px', marginBottom: '4px', background: 'linear-gradient(135deg, #F59E0B, #D97706)', color: '#fff', padding: '1px 6px', borderRadius: '8px', fontSize: '9px', fontWeight: 700, whiteSpace: 'nowrap', width: 'fit-content' }}><FiStar size={8} fill="#fff" /> FOUNDING</span>}
        <p style={{ color: 'var(--gray)', fontSize: '12px', marginBottom: '8px', lineHeight: 1.4, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
          {vendor.business_description || 'Professional vendor services'}
        </p>
        <div className="vendor-meta" style={{ display: 'flex', gap: '12px', fontSize: '12px', color: 'var(--gray)', flexWrap: 'wrap', alignItems: 'center' }}>
          {vendor.business_location && <span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}><FiMapPin size={11} /> {vendor.business_location}</span>}
          {rating > 0 && (
            <span style={{ display: 'flex', alignItems: 'center', gap: '3px', fontWeight: 600, color: 'var(--dark)' }}>
              <FiStar size={11} style={{ color: '#F59E0B', fill: '#F59E0B' }} /> {rating.toFixed(1)}
              <span style={{ fontWeight: 400, color: 'var(--gray)' }}>({vendor.review_count})</span>
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}

export default function Vendors() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const fromEvent = location.state?.fromEvent;
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filter states
  const [search, setSearch] = useState('');
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [priceRange, setPriceRange] = useState(null);
  const [locationQuery, setLocationQuery] = useState('');
  const [selectedEventTypes, setSelectedEventTypes] = useState([]);
  const [minRating, setMinRating] = useState(0);
  const [openFilter, setOpenFilter] = useState(null);
  const [sortBy, setSortBy] = useState('relevance'); // relevance | rating | price_asc | price_desc | name

  useEffect(() => { loadVendors(); }, []);

  const loadVendors = async () => {
    try {
      const res = await api.get('/vendors');
      setVendors(res.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const applyFilters = useCallback(() => {
    let result = [...vendors];

    // Search
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(v =>
        (v.business_name || v.name || '').toLowerCase().includes(q) ||
        (v.business_description || '').toLowerCase().includes(q) ||
        (v.business_category || '').toLowerCase().includes(q)
      );
    }

    // Categories (multi-select)
    if (selectedCategories.length > 0) {
      result = result.filter(v => selectedCategories.includes(v.business_category));
    }

    // Price range
    if (priceRange) {
      result = result.filter(v => {
        const max = parsePriceMax(v.business_price_range);
        if (max === 0) return false;
        return max >= priceRange.min && max <= priceRange.max;
      });
    }

    // Location (with proximity matching + handles array service_areas)
    if (locationQuery.trim()) {
      result = result.filter(v => locationMatches(v.business_location, v.service_areas, locationQuery.trim()));
    }

    // Event types (multi-select) — empty served_event_types array means vendor serves ALL event types
    if (selectedEventTypes.length > 0) {
      result = result.filter(v => {
        const served = v.served_event_types || [];
        if (served.length === 0) return true; // Vendor serves all events
        return selectedEventTypes.some(et => served.includes(et));
      });
    }

    // Star rating — show vendors with avg_rating >= minRating
    if (minRating > 0) {
      result = result.filter(v => Number(v.avg_rating || 0) >= minRating);
    }

    // Sort
    switch (sortBy) {
      case 'rating':
        result.sort((a, b) => (Number(b.avg_rating) || 0) - (Number(a.avg_rating) || 0));
        break;
      case 'price_asc':
        result.sort((a, b) => parsePriceMax(a.business_price_range) - parsePriceMax(b.business_price_range));
        break;
      case 'price_desc':
        result.sort((a, b) => parsePriceMax(b.business_price_range) - parsePriceMax(a.business_price_range));
        break;
      case 'name':
        result.sort((a, b) => (a.business_name || a.name || '').localeCompare(b.business_name || b.name || ''));
        break;
      default: // relevance — keep API order
        break;
    }

    return result;
  }, [vendors, search, selectedCategories, priceRange, locationQuery, selectedEventTypes, minRating, sortBy]);

  const filtered = applyFilters();
  const hasActiveFilters = selectedCategories.length > 0 || priceRange || locationQuery.trim() || selectedEventTypes.length > 0 || minRating > 0;

  const toggleCategory = (cat) => {
    setSelectedCategories(prev => prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]);
  };

  const toggleEventType = (et) => {
    setSelectedEventTypes(prev => prev.includes(et) ? prev.filter(e => e !== et) : [...prev, et]);
  };

  const toggleFilter = (name) => {
    setOpenFilter(openFilter === name ? null : name);
  };

  const clearAllFilters = () => {
    setSelectedCategories([]);
    setPriceRange(null);
    setLocationQuery('');
    setSelectedEventTypes([]);
    setMinRating(0);
    setOpenFilter(null);
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--cream)' }}>
      <style>{`
        @media (max-width: 768px) {
          .filter-pills { flex-wrap: nowrap !important; overflow-x: auto !important; WebkitOverflowScrolling: touch; mask-image: linear-gradient(to right, black calc(100% - 32px), transparent 100%); -webkit-mask-image: linear-gradient(to right, black calc(100% - 32px), transparent 100%); padding-bottom: 4px; }
          .filter-pills > * { flex-shrink: 0; white-space: nowrap; }
          .vendor-grid { grid-template-columns: repeat(2, 1fr) !important; gap: 10px !important; }
          .vendor-grid .card { min-height: 260px !important; }
          .vendor-grid .card-body { padding: 10px 12px !important; }
          .vendor-grid h3 { font-size: 13px !important; }
          .vendor-grid p { display: none !important; }
          .vendor-grid .vendor-meta { font-size: 10px !important; gap: 6px !important; }
        }
        @media (min-width: 769px) and (max-width: 1024px) {
          .vendor-grid { grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)) !important; }
        }
      `}</style>
      <nav style={{ background: 'transparent', padding: '16px 0', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)', backgroundColor: 'rgba(247,247,248,0.8)', position: 'sticky', top: 0, zIndex: 100 }}>
        <div className="container" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <button onClick={() => fromEvent ? navigate(`/events/${fromEvent}`) : navigate('/dashboard')} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', color: 'var(--dark)', fontSize: '14px', background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontWeight: 500 }}>
            <FiArrowLeft size={18} /> Back
          </button>
          <div style={{ display: 'flex', gap: '12px' }}>
            {user ? (
              <Link to={user.role === 'vendor' ? '/vendor-dashboard' : '/dashboard'} className="btn btn-primary btn-sm">My Dashboard</Link>
            ) : (
              <>
                <Link to="/register/vendor" className="btn btn-outline btn-sm">Join as Vendor</Link>
                <Link to="/login" className="btn btn-primary btn-sm">Sign In</Link>
              </>
            )}
          </div>
        </div>
      </nav>

      <div className="container" style={{ padding: '32px 20px' }}>
        <div style={{ marginBottom: '32px' }}>
          <h2 style={{ fontSize: '32px', marginBottom: '8px' }}>Vendor Marketplace</h2>
          <p style={{ color: 'var(--gray)' }}>Find trusted vendors for your celebration</p>
        </div>

        {/* Search Bar */}
        <div style={{ marginBottom: '20px' }}>
          <div style={{ position: 'relative', maxWidth: '500px' }}>
            <FiSearch style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: 'var(--gray)' }} />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search vendors by name, description, or category..."
              style={{ paddingLeft: '40px', width: '100%' }}
            />
          </div>
        </div>

        {/* Category Quick-Chips — always visible horizontal scroll */}
        <div className="filter-pills" style={{ display: 'flex', gap: '8px', marginBottom: '14px', flexWrap: 'nowrap', overflowX: 'auto', WebkitOverflowScrolling: 'touch', paddingBottom: '2px' }}>
          {ALL_CATEGORIES.map(cat => (
            <button
              key={cat}
              onClick={() => toggleCategory(cat)}
              style={{
                flexShrink: 0, whiteSpace: 'nowrap', padding: '7px 16px', borderRadius: '20px',
                border: '1.5px solid',
                borderColor: selectedCategories.includes(cat) ? 'var(--rose-deep)' : 'var(--cream-dark)',
                background: selectedCategories.includes(cat) ? 'var(--rose-deep)' : 'var(--white)',
                color: selectedCategories.includes(cat) ? 'white' : 'var(--dark)',
                cursor: 'pointer', fontSize: '13px', fontWeight: 500,
                transition: 'all 0.2s'
              }}
            >
              {selectedCategories.includes(cat) && <FiCheckCircle size={11} style={{ marginRight: '4px' }} />}
              {cat}
            </button>
          ))}
        </div>

        {/* Filter Toggle Buttons */}
        <div className="filter-pills" style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
          <button
            onClick={() => toggleFilter('category')}
            style={{
              display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px', borderRadius: '24px', border: '1.5px solid',
              borderColor: openFilter === 'category' ? 'var(--rose-deep)' : selectedCategories.length > 0 ? 'var(--rose-deep)' : 'var(--cream-dark)',
              background: openFilter === 'category' ? 'rgba(201, 68, 112, 0.08)' : selectedCategories.length > 0 ? 'rgba(201, 68, 112, 0.05)' : 'var(--white)',
              cursor: 'pointer', fontSize: '13px', fontWeight: 500, color: selectedCategories.length > 0 ? 'var(--rose-deep)' : 'var(--dark)',
              transition: 'all 0.2s'
            }}
          >
            <FiFilter size={14} />
            Categories{selectedCategories.length > 0 ? ` (${selectedCategories.length})` : ''}
          </button>

          <button
            onClick={() => toggleFilter('price')}
            style={{
              display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px', borderRadius: '24px', border: '1.5px solid',
              borderColor: openFilter === 'price' ? 'var(--rose-deep)' : priceRange ? 'var(--rose-deep)' : 'var(--cream-dark)',
              background: openFilter === 'price' ? 'rgba(201, 68, 112, 0.08)' : priceRange ? 'rgba(201, 68, 112, 0.05)' : 'var(--white)',
              cursor: 'pointer', fontSize: '13px', fontWeight: 500, color: priceRange ? 'var(--rose-deep)' : 'var(--dark)',
              transition: 'all 0.2s'
            }}
          >
            <CoinIcon size={14} />
            Price{priceRange ? `: ${priceRange.label}` : ''}
          </button>

          <button
            onClick={() => toggleFilter('location')}
            style={{
              display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px', borderRadius: '24px', border: '1.5px solid',
              borderColor: openFilter === 'location' ? 'var(--rose-deep)' : locationQuery.trim() ? 'var(--rose-deep)' : 'var(--cream-dark)',
              background: openFilter === 'location' ? 'rgba(201, 68, 112, 0.08)' : locationQuery.trim() ? 'rgba(201, 68, 112, 0.05)' : 'var(--white)',
              cursor: 'pointer', fontSize: '13px', fontWeight: 500, color: locationQuery.trim() ? 'var(--rose-deep)' : 'var(--dark)',
              transition: 'all 0.2s'
            }}
          >
            <FiMapPin size={14} />
            Location{locationQuery.trim() ? `: ${locationQuery.trim()}` : ''}
          </button>

          <button
            onClick={() => toggleFilter('event')}
            style={{
              display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px', borderRadius: '24px', border: '1.5px solid',
              borderColor: openFilter === 'event' ? 'var(--rose-deep)' : selectedEventTypes.length > 0 ? 'var(--rose-deep)' : 'var(--cream-dark)',
              background: openFilter === 'event' ? 'rgba(201, 68, 112, 0.08)' : selectedEventTypes.length > 0 ? 'rgba(201, 68, 112, 0.05)' : 'var(--white)',
              cursor: 'pointer', fontSize: '13px', fontWeight: 500, color: selectedEventTypes.length > 0 ? 'var(--rose-deep)' : 'var(--dark)',
              transition: 'all 0.2s'
            }}
          >
            <FiSliders size={14} />
            Event Type{selectedEventTypes.length > 0 ? ` (${selectedEventTypes.length})` : ''}
          </button>

          <button
            onClick={() => toggleFilter('rating')}
            style={{
              display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px', borderRadius: '24px', border: '1.5px solid',
              borderColor: openFilter === 'rating' ? 'var(--rose-deep)' : minRating > 0 ? 'var(--rose-deep)' : 'var(--cream-dark)',
              background: openFilter === 'rating' ? 'rgba(201, 68, 112, 0.08)' : minRating > 0 ? 'rgba(201, 68, 112, 0.05)' : 'var(--white)',
              cursor: 'pointer', fontSize: '13px', fontWeight: 500, color: minRating > 0 ? 'var(--rose-deep)' : 'var(--dark)',
              transition: 'all 0.2s'
            }}
          >
            <FiStar size={14} style={{ color: minRating > 0 ? '#F59E0B' : undefined, fill: minRating > 0 ? '#F59E0B' : undefined }} />
            Rating{minRating > 0 ? `: ${minRating}+ ★` : ''}
          </button>

          {hasActiveFilters && (
            <button
              onClick={clearAllFilters}
              style={{
                display: 'flex', alignItems: 'center', gap: '4px', padding: '8px 14px', borderRadius: '24px',
                border: '1px solid var(--cream-dark)', background: 'transparent', cursor: 'pointer',
                fontSize: '12px', color: 'var(--gray)', transition: 'all 0.2s'
              }}
            >
              <FiX size={14} /> Clear All
            </button>
          )}
        </div>

        {/* Active Filter Tags (inline, when no panel is open) */}
        {hasActiveFilters && !openFilter && (
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '16px' }}>
            {selectedCategories.map(cat => (
              <span key={cat} onClick={() => toggleCategory(cat)} style={{
                display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '3px 10px',
                background: 'rgba(201, 68, 112, 0.1)', color: 'var(--rose-deep)', borderRadius: '14px',
                fontSize: '11px', cursor: 'pointer', fontWeight: 500
              }}>
                {cat} <FiX size={10} />
              </span>
            ))}
            {priceRange && (
              <span onClick={() => setPriceRange(null)} style={{
                display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '3px 10px',
                background: 'rgba(201, 68, 112, 0.1)', color: 'var(--rose-deep)', borderRadius: '14px',
                fontSize: '11px', cursor: 'pointer', fontWeight: 500
              }}>
                {priceRange.label} <FiX size={10} />
              </span>
            )}
            {locationQuery.trim() && (
              <span onClick={() => setLocationQuery('')} style={{
                display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '3px 10px',
                background: 'rgba(201, 68, 112, 0.1)', color: 'var(--rose-deep)', borderRadius: '14px',
                fontSize: '11px', cursor: 'pointer', fontWeight: 500
              }}>
                {locationQuery.trim()} <FiX size={10} />
              </span>
            )}
            {selectedEventTypes.map(et => (
              <span key={et} onClick={() => toggleEventType(et)} style={{
                display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '3px 10px',
                background: 'rgba(201, 68, 112, 0.1)', color: 'var(--rose-deep)', borderRadius: '14px',
                fontSize: '11px', cursor: 'pointer', fontWeight: 500
              }}>
                {et} <FiX size={10} />
              </span>
            ))}
            {minRating > 0 && (
              <span onClick={() => setMinRating(0)} style={{
                display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '3px 10px',
                background: 'rgba(245, 158, 11, 0.12)', color: '#D97706', borderRadius: '14px',
                fontSize: '11px', cursor: 'pointer', fontWeight: 500
              }}>
                ★ {minRating}+ <FiX size={10} />
              </span>
            )}
          </div>
        )}

        {/* Category Filter Panel */}
        {openFilter === 'category' && (
          <div className="card" style={{ padding: '16px 20px', marginBottom: '16px', background: 'var(--white)', borderRadius: '12px', border: '1px solid var(--cream-dark)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <span style={{ fontWeight: 600, fontSize: '14px' }}>Categories</span>
              <button onClick={() => { setSelectedCategories([]); setOpenFilter(null); }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px', color: 'var(--gray)' }}>Reset</button>
            </div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {ALL_CATEGORIES.map(cat => (
                <button key={cat} onClick={() => toggleCategory(cat)} style={{
                  padding: '6px 14px', borderRadius: '20px', border: '1px solid',
                  borderColor: selectedCategories.includes(cat) ? 'var(--rose-deep)' : 'var(--cream-dark)',
                  background: selectedCategories.includes(cat) ? 'var(--rose-deep)' : 'var(--white)',
                  color: selectedCategories.includes(cat) ? 'white' : 'var(--dark)',
                  cursor: 'pointer', fontSize: '12px', fontWeight: selectedCategories.includes(cat) ? 600 : 400,
                  transition: 'all 0.15s', display: 'flex', alignItems: 'center', gap: '4px'
                }}>
                  {selectedCategories.includes(cat) && <FiCheckCircle size={12} />}
                  {cat}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Price Filter Panel */}
        {openFilter === 'price' && (
          <div className="card" style={{ padding: '16px 20px', marginBottom: '16px', background: 'var(--white)', borderRadius: '12px', border: '1px solid var(--cream-dark)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <span style={{ fontWeight: 600, fontSize: '14px' }}>Price Range</span>
              <button onClick={() => { setPriceRange(null); setOpenFilter(null); }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px', color: 'var(--gray)' }}>Reset</button>
            </div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {PRICE_RANGES.map(pr => (
                <button key={pr.label} onClick={() => setPriceRange(priceRange?.label === pr.label ? null : pr)} style={{
                  padding: '6px 14px', borderRadius: '20px', border: '1px solid',
                  borderColor: priceRange?.label === pr.label ? 'var(--rose-deep)' : 'var(--cream-dark)',
                  background: priceRange?.label === pr.label ? 'var(--rose-deep)' : 'var(--white)',
                  color: priceRange?.label === pr.label ? 'white' : 'var(--dark)',
                  cursor: 'pointer', fontSize: '12px', fontWeight: priceRange?.label === pr.label ? 600 : 400,
                  transition: 'all 0.15s'
                }}>
                  {pr.label} {pr.max === Infinity ? '(R100k+)' : `(R${(pr.min/1000).toFixed(0)}k - R${(pr.max/1000).toFixed(0)}k)`}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Location Filter Panel */}
        {openFilter === 'location' && (
          <div className="card" style={{ padding: '16px 20px', marginBottom: '16px', background: 'var(--white)', borderRadius: '12px', border: '1px solid var(--cream-dark)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <span style={{ fontWeight: 600, fontSize: '14px' }}>Location</span>
              <button onClick={() => { setLocationQuery(''); setOpenFilter(null); }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px', color: 'var(--gray)' }}>Reset</button>
            </div>
            <div style={{ position: 'relative', maxWidth: '400px' }}>
              <FiMapPin style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--gray)' }} />
              <input
                value={locationQuery}
                onChange={e => setLocationQuery(e.target.value)}
                placeholder="Search by city or area (e.g., Pretoria, Ga-Rankuwa, Sandton, Soweto)..."
                style={{ paddingLeft: '36px', width: '100%' }}
              />
            </div>
          </div>
        )}

        {/* Rating Filter Panel */}
        {openFilter === 'rating' && (
          <div className="card" style={{ padding: '16px 20px', marginBottom: '16px', background: 'var(--white)', borderRadius: '12px', border: '1px solid var(--cream-dark)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <span style={{ fontWeight: 600, fontSize: '14px' }}>Minimum Rating</span>
              <button onClick={() => { setMinRating(0); setOpenFilter(null); }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px', color: 'var(--gray)' }}>Reset</button>
            </div>
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
              {[1, 2, 3, 4, 5].map(star => (
                <button
                  key={star}
                  onClick={() => setMinRating(minRating === star ? 0 : star)}
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer', padding: '4px',
                    fontSize: '28px', lineHeight: 1, transition: 'transform 0.15s',
                    transform: minRating >= star ? 'scale(1.1)' : 'scale(1)'
                  }}
                  onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.2)'}
                  onMouseLeave={e => e.currentTarget.style.transform = minRating >= star ? 'scale(1.1)' : 'scale(1)'}
                  title={`${star}+ stars`}
                >
                  <FiStar
                    size={28}
                    style={{
                      color: minRating >= star ? '#F59E0B' : 'var(--cream-dark)',
                      fill: minRating >= star ? '#F59E0B' : 'none',
                      transition: 'all 0.15s'
                    }}
                  />
                </button>
              ))}
              {minRating > 0 && (
                <span style={{ marginLeft: '8px', fontSize: '14px', color: 'var(--gray)', fontWeight: 500 }}>
                  {minRating}+ stars
                </span>
              )}
            </div>
          </div>
        )}

        {/* Event Type Filter Panel */}
        {openFilter === 'event' && (
          <div className="card" style={{ padding: '16px 20px', marginBottom: '16px', background: 'var(--white)', borderRadius: '12px', border: '1px solid var(--cream-dark)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <span style={{ fontWeight: 600, fontSize: '14px' }}>Event Types Served</span>
              <button onClick={() => { setSelectedEventTypes([]); setOpenFilter(null); }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px', color: 'var(--gray)' }}>Reset</button>
            </div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {EVENT_TYPES.map(et => (
                <button key={et} onClick={() => toggleEventType(et)} style={{
                  padding: '6px 14px', borderRadius: '20px', border: '1px solid',
                  borderColor: selectedEventTypes.includes(et) ? 'var(--rose-deep)' : 'var(--cream-dark)',
                  background: selectedEventTypes.includes(et) ? 'var(--rose-deep)' : 'var(--white)',
                  color: selectedEventTypes.includes(et) ? 'white' : 'var(--dark)',
                  cursor: 'pointer', fontSize: '12px', fontWeight: selectedEventTypes.includes(et) ? 600 : 400,
                  transition: 'all 0.15s', display: 'flex', alignItems: 'center', gap: '4px'
                }}>
                  {selectedEventTypes.includes(et) && <FiCheckCircle size={12} />}
                  {et}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Results Count + Sort */}
        <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
          <span style={{ fontSize: '13px', color: 'var(--gray)' }}>
            {filtered.length} vendor{filtered.length !== 1 ? 's' : ''} found
            {hasActiveFilters && ' matching your filters'}
          </span>
          <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
            <select
              value={sortBy}
              onChange={e => setSortBy(e.target.value)}
              style={{
                appearance: 'none', WebkitAppearance: 'none', MozAppearance: 'none',
                padding: '8px 36px 8px 14px', borderRadius: '8px',
                border: '1.5px solid var(--cream-dark)', background: 'var(--white)',
                color: 'var(--dark)', fontSize: '13px', fontWeight: 500,
                cursor: 'pointer', minWidth: '180px'
              }}
            >
              <option value="relevance">Sort: Relevance</option>
              <option value="rating">Sort: Highest Rated</option>
              <option value="price_asc">Sort: Price Low→High</option>
              <option value="price_desc">Sort: Price High→Low</option>
              <option value="name">Sort: Name A→Z</option>
            </select>
            <FiChevronDown size={14} style={{ position: 'absolute', right: '12px', pointerEvents: 'none', color: 'var(--gray)' }} />
          </div>
        </div>


        {/* Vendors Grid */}
        {loading ? (
          <div className="card" style={{ textAlign: 'center', padding: '60px', color: 'var(--gray)' }}>Loading vendors...</div>
        ) : filtered.length === 0 ? (
          <div className="card" style={{ textAlign: 'center', padding: '60px' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🏪</div>
            <h3>No Vendors Found</h3>
            <p style={{ color: 'var(--gray)', marginTop: '8px' }}>No vendors match your filters. Try adjusting your search criteria.</p>
          </div>
        ) : (
          <div className="vendor-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(270px, 1fr))', gap: '20px' }}>
            {filtered.map((v, i) => <VendorCard key={v.id} vendor={v} staggerIndex={i} />)}
          </div>
        )}
      </div>
    </div>
  );
}
